import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, Plus, X } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import AiContentChip from '../ui/AiContentChip';
import { PromptModal } from '../ui/Modal';
import { formatDay } from './docMetaFormat';
import { revealInSidebar } from './revealInSidebar';
import useScopeName from './useScopeName';
import { useDocsLayoutStore } from './docsLayoutStore';
import './TitleBlock.css';

/**
 * Above and under the document title (card 2.3): chips for the containing
 * folder and the document's tags (+ one to add a tag), the title input, and
 * the author line "Samin · created 28 Sep 2026 · last edited 7 Oct 2026".
 *
 * - Folder chip → reveals the folder in the sidebar.
 * - Tag chip → filters the sidebar search by the tag ("#tag"); its × removes it.
 * - "+" → a modal-input for a new tag.
 * - Author = the record's `createdBy`, else the signed-in user, else hidden;
 *   "created …" only for documents that carry `createdAt`.
 */
export default function TitleBlock({ title, onTitleChange, ancestors, scope, meta, userName, readOnly = false }) {
  const { t, i18n } = useTranslation();
  const [adding, setAdding] = useState(false);
  const requestSidebarSearch = useDocsLayoutStore((s) => s.requestSidebarSearch);
  const titleRef = useRef(null);

  // The title wraps like the artboard's two-line h1: a textarea that grows
  // with its content.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    // A narrower column wraps the title onto more lines. Only a WIDTH change
    // refits — reacting to the height this sets would loop.
    let width = el.clientWidth;
    const onResize = () => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      fit();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [title]);

  const folderIndex = (() => {
    for (let i = ancestors.length - 1; i >= 0; i -= 1) if (ancestors[i].type === 'folder') return i;
    return -1;
  })();
  const folder = folderIndex >= 0 ? ancestors[folderIndex] : null;
  // In a user workspace the folder chip reads "Workspace › Folder" (card 5.3);
  // Personal keeps the plain folder name of the artboard.
  const scopeName = useScopeName(scope);
  const inWorkspace = !!scope?.scopeId && (!scope?.type || scope.type === 'personal');
  const folderName = folder ? folder.name || t('docEditor.untitledFolder') : '';
  const folderLabel = inWorkspace ? `${scopeName} › ${folderName}` : folderName;
  const tags = meta.tags || [];
  const author = meta.createdBy?.name || userName || '';
  const lang = i18n.language || 'en';
  const parts = [
    author,
    meta.createdAt ? t('docEditor.title.created', { date: formatDay(meta.createdAt, lang) }) : null,
    meta.editedAt ? t('docEditor.title.lastEdited', { date: formatDay(meta.editedAt, lang) }) : null,
  ].filter(Boolean);

  const removeTag = (tag) => meta.setTags(tags.filter((x) => x !== tag));

  return (
    <div className="doc-title-block">
      <div className="doc-chips" data-testid="doc-chips">
        {folder && (
          <Tooltip content={t('docEditor.title.revealFolder')} side="bottom">
            <button
              type="button"
              className="doc-chip doc-chip--folder"
              aria-label={folderLabel}
              onClick={() =>
                revealInSidebar(scope, folder.id, ancestors.slice(0, folderIndex + 1).map((n) => n.id))
              }
            >
              <Folder size={12} strokeWidth={1.6} aria-hidden />
              <span className="doc-chip-text">{folderLabel}</span>
            </button>
          </Tooltip>
        )}
        {/* AI Act Art. 50: created or edited by an AI (aiAuthorship.js). */}
        <AiContentChip mark={meta.aiMark} className="doc-ai-chip" />
        {tags.map((tag) => (
          <span key={tag} className="doc-chip doc-chip--tag">
            <Tooltip content={t('docEditor.title.filterByTag', { tag })} side="bottom">
              <button type="button" className="doc-chip-main" aria-label={tag} onClick={() => requestSidebarSearch(`#${tag}`)}>
                <span className="doc-chip-text">{tag}</span>
              </button>
            </Tooltip>
            <button
              type="button"
              className="doc-chip-remove"
              aria-label={t('docEditor.title.removeTag', { tag })}
              onClick={() => removeTag(tag)}
            >
              <X size={11} strokeWidth={2} aria-hidden />
            </button>
          </span>
        ))}
        <Tooltip content={t('docEditor.title.addTag')} side="bottom">
          <button
            type="button"
            className="doc-chip doc-chip--add"
            aria-label={t('docEditor.title.addTag')}
            onClick={() => setAdding(true)}
          >
            <Plus size={12} strokeWidth={1.8} aria-hidden />
          </button>
        </Tooltip>
      </div>
      <textarea
        ref={titleRef}
        rows={1}
        className="doc-editor-title"
        value={title}
        onChange={onTitleChange}
        readOnly={readOnly}
        onKeyDown={(e) => {
          // A title is one line: Enter goes on into the body, as in Notion.
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            e.currentTarget.closest('.doc-reading-column')?.querySelector('[contenteditable="true"]')?.focus();
          }
        }}
        placeholder={t('docEditor.untitled')}
        spellCheck={false}
        aria-label={t('docEditor.title.label')}
      />
      {parts.length > 0 && (
        <p className="doc-author-line" data-testid="doc-author-line">
          {parts.join(' · ')}
        </p>
      )}
      <PromptModal
        isOpen={adding}
        onClose={() => setAdding(false)}
        onConfirm={(tag) => {
          setAdding(false);
          meta.setTags([...tags, tag]);
        }}
        title={t('docEditor.title.addTagTitle')}
        placeholder={t('docEditor.title.addTagPlaceholder')}
        submitLabel={t('docEditor.title.addTagSubmit')}
      />
    </div>
  );
}
